/**
 * 🎯 SPACE PROSPECTS - Module Radar (Prospects)
 * Gestion des prospects email style spatial
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Target, Mail, Send, Users, Check, Eye, MapPin, Phone,
  Loader2, RefreshCw, Gift, Clock, CheckCircle, AlertCircle, Zap
} from 'lucide-react';
import { Button } from '../ui/button';
import { api } from '../../services/api';
import { toast } from 'sonner';
import ProspectsDashboard from './ProspectsDashboard';

// Types d'emails
const EMAIL_TYPES = [
  { type: 'welcome', label: '🎁 Email de Bienvenue', desc: 'Avec code promo unique', color: 'green' },
  { type: 'products', label: '🛒 Nos Produits', desc: 'Puffs, CBD, Boissons', color: 'cyan' },
  { type: 'delivery', label: '🚚 Livraison Gratuite', desc: 'Infos livraison', color: 'orange' },
  { type: 'reminder', label: '⏰ Rappel Code Promo', desc: 'Urgence : code expire', color: 'red' },
];

// Carte prospect
const ProspectCard = ({ prospect, isSelected, onSelect }) => {
  const isNew = !prospect.viewed && prospect.confirmed;
  const isConverted = prospect.promoUsed;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.01 }}
      onClick={onSelect}
      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
        isSelected
          ? 'bg-cyan-500/20 border-cyan-500'
          : isNew
            ? 'bg-orange-500/10 border-orange-500/50'
            : 'bg-[#1a1a25] border-gray-700 hover:border-cyan-500/50'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <input 
            type="checkbox" 
            checked={isSelected}
            onChange={() => {}}
            className="w-4 h-4 accent-cyan-500"
          />
          <div>
            <div className="font-bold text-white flex items-center gap-2">
              {prospect.prenom}
              {isNew && <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />}
            </div>
            <div className="text-sm text-gray-400">{prospect.email}</div>
            <div className="text-xs text-gray-500 flex items-center gap-2 mt-1">
              {prospect.codePostal && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {prospect.codePostal}
                </span>
              )}
              {prospect.telephone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {prospect.telephone}
                </span>
              )}
            </div>
            {/* Code promo */}
            {prospect.codePromo && prospect.confirmed && (
              <div className="mt-2">
                <span className={`px-2 py-0.5 rounded text-xs font-mono ${
                  isConverted 
                    ? 'bg-gray-500/30 text-gray-400 line-through' 
                    : 'bg-green-500/20 text-green-400'
                }`}>
                  🎫 {prospect.codePromo}
                  {isConverted && ' (utilisé)'}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500">
            {new Date(prospect.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
          </div>
          {/* Statut */}
          <div className="mt-1">
            {!prospect.confirmed ? (
              <span className="px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 text-xs flex items-center gap-1">
                <Clock className="w-3 h-3" />
                En attente
              </span>
            ) : isConverted ? (
              <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 text-xs flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Converti
              </span>
            ) : (
              <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs flex items-center gap-1">
                <Check className="w-3 h-3" />
                Confirmé
              </span>
            )}
          </div>
          {/* Emails envoyés */}
          <div className="flex gap-1 mt-1 flex-wrap justify-end">
            {(prospect.emailsSent || []).map(email => (
              <span key={email} className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 text-xs">
                📧 {email}
              </span>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Module Prospects principal
const SpaceProspects = () => {
  const [prospects, setProspects] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isTriggeringCron, setIsTriggeringCron] = useState(false);

  // Charger les prospects
  const loadProspects = async () => {
    setIsLoading(true);
    try {
      const data = await api.getProspects?.() || [];
      const list = Array.isArray(data) ? data : data.prospects || [];
      setProspects(list);
    } catch (error) {
      console.error('Erreur chargement prospects:', error);
      setProspects([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProspects();
  }, []);

  // Toggle sélection
  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // Sélectionner tous
  const selectAll = () => {
    setSelectedIds(prospects.map(p => p.id));
  };

  // Désélectionner tous
  const deselectAll = () => {
    setSelectedIds([]);
  };

  // Marquer tous comme vus
  const markAllViewed = async () => {
    try {
      await api.markAllProspectsViewed?.();
      toast.success('✓ Tous marqués comme vus');
      loadProspects();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Lancer les relances automatiques (CRON)
  const handleTriggerCron = async () => {
    setIsTriggeringCron(true);
    try {
      const result = await api.triggerCronReminders?.();
      if (result?.success) {
        const { totalSent, expired, details } = result.summary || {};
        toast.success(`🚀 Relances envoyées !`, {
          description: `${totalSent || 0} emails • ${expired || 0} expirés`,
          duration: 5000
        });
        loadProspects();
      } else {
        toast.error('Erreur lors des relances');
      }
    } catch (error) {
      toast.error('Erreur lors du lancement des relances');
    } finally {
      setIsTriggeringCron(false);
    }
  };

  // Envoyer email
  const sendEmail = async (emailType) => {
    if (selectedIds.length === 0) {
      toast.error('Sélectionnez au moins un prospect');
      return;
    }

    setIsSending(true);
    try {
      const result = await api.sendBulkProspectEmails?.(selectedIds, emailType);
      toast.success(`📧 ${result?.sent || selectedIds.length} email(s) envoyé(s) !`);
      setSelectedIds([]);
      loadProspects();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setIsSending(false);
    }
  };

  // Stats
  const newProspects = prospects.filter(p => !p.viewed && p.confirmed).length;
  const confirmedProspects = prospects.filter(p => p.confirmed).length;
  const convertedProspects = prospects.filter(p => p.promoUsed).length;

  // Trier les prospects
  const sortedProspects = [...prospects].sort((a, b) => {
    if (!a.viewed && b.viewed) return -1;
    if (a.viewed && !b.viewed) return 1;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
            🎯 RADAR
          </h1>
          <p className="text-gray-500 text-sm">Prospects et acquisition</p>
        </div>
        <Button
          onClick={loadProspects}
          variant="outline"
          className="border-cyan-500/30 text-cyan-400"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="backdrop-blur-xl rounded-2xl p-4 border border-orange-500/30 bg-orange-500/5">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-orange-400" />
            <div>
              <p className="text-2xl font-bold text-orange-400">{newProspects}</p>
              <p className="text-xs text-gray-500">Nouveaux</p>
            </div>
          </div>
        </div>
        <div className="backdrop-blur-xl rounded-2xl p-4 border border-green-500/30 bg-green-500/5">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-400" />
            <div>
              <p className="text-2xl font-bold text-green-400">{confirmedProspects}</p>
              <p className="text-xs text-gray-500">Confirmés</p>
            </div>
          </div>
        </div>
        <div className="backdrop-blur-xl rounded-2xl p-4 border border-purple-500/30 bg-purple-500/5">
          <div className="flex items-center gap-3">
            <Gift className="w-6 h-6 text-purple-400" />
            <div>
              <p className="text-2xl font-bold text-purple-400">{convertedProspects}</p>
              <p className="text-xs text-gray-500">Convertis</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Liste des prospects */}
        <div className="lg:col-span-2 backdrop-blur-xl rounded-2xl p-5 border border-cyan-500/30 bg-[#12121a]/60">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-cyan-400" />
              📧 Prospects ({prospects.length})
            </h3>
            <div className="flex gap-2">
              <Button
                onClick={selectAll}
                variant="outline"
                size="sm"
                className="text-cyan-400 border-cyan-500/30"
              >
                Tout sélectionner
              </Button>
              <Button
                onClick={deselectAll}
                variant="outline"
                size="sm"
                className="text-gray-400 border-gray-600"
              >
                Désélectionner
              </Button>
              {newProspects > 0 && (
                <Button
                  onClick={markAllViewed}
                  variant="outline"
                  size="sm"
                  className="text-green-400 border-green-500/30"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Marquer vus
                </Button>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            </div>
          ) : prospects.length === 0 ? (
            <div className="text-center py-12">
              <Target className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500">Aucun prospect pour le moment</p>
              <p className="text-gray-600 text-sm mt-2">
                Partagez <span className="text-cyan-400">/offre-bienvenue</span> pour capturer des emails !
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {sortedProspects.map((prospect) => (
                <ProspectCard
                  key={prospect.id}
                  prospect={prospect}
                  isSelected={selectedIds.includes(prospect.id)}
                  onSelect={() => toggleSelect(prospect.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Actions emails */}
        <div className="backdrop-blur-xl rounded-2xl p-5 border border-orange-500/30 bg-[#12121a]/60">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Send className="w-5 h-5 text-orange-400" />
            📨 Actions Email
          </h3>

          {/* Bouton CRON Relances Auto */}
          <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30">
            <Button
              onClick={handleTriggerCron}
              disabled={isTriggeringCron}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
            >
              {isTriggeringCron ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  🚀 Lancer Relances Auto
                </>
              )}
            </Button>
            <p className="text-xs text-gray-500 mt-2 text-center">
              J+3, J+7, J+10, J+13 automatiques
            </p>
          </div>

          <div className="border-t border-gray-700 my-4" />

          <p className="text-sm text-gray-400 mb-4">
            {selectedIds.length > 0 
              ? `${selectedIds.length} prospect(s) sélectionné(s)`
              : 'Sélectionnez des prospects'
            }
          </p>

          <div className="space-y-3">
            {EMAIL_TYPES.map(({ type, label, desc, color }) => {
              const colorClasses = {
                green: 'border-green-500/30 hover:border-green-500 hover:bg-green-500/10',
                cyan: 'border-cyan-500/30 hover:border-cyan-500 hover:bg-cyan-500/10',
                orange: 'border-orange-500/30 hover:border-orange-500 hover:bg-orange-500/10',
                red: 'border-red-500/30 hover:border-red-500 hover:bg-red-500/10',
              };

              return (
                <Button
                  key={type}
                  onClick={() => sendEmail(type)}
                  disabled={isSending || selectedIds.length === 0}
                  variant="outline"
                  className={`w-full justify-start text-left h-auto py-3 ${colorClasses[color]}`}
                >
                  <div>
                    <div className="font-bold text-white">{label}</div>
                    <div className="text-xs text-gray-400">{desc}</div>
                  </div>
                </Button>
              );
            })}
          </div>

          {isSending && (
            <div className="mt-4 flex items-center justify-center gap-2 text-cyan-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Envoi en cours...</span>
            </div>
          )}
        </div>
      </div>

      {/* 📊 Dashboard Prospects -10% */}
      <div className="mt-8">
        <ProspectsDashboard />
      </div>
    </div>
  );
};

export default SpaceProspects;
